import {useCallback, useMemo, useState} from "react";
import {IProtocolStep, IProtocolStepKind, IProtocolStepStatus} from "@/protocols/types/protocol.ts";

/**
 * Navigation state and actions for step-by-step protocol navigation.
 */
export interface UseStepNavigationOptions {
    orderedSteps: IProtocolStep[];
    onExitFocusMode?: () => void;
}

export interface UseStepNavigationResult {
    /** Current active step index */
    activeStepIndex: number;
    /** Set active step index directly (for external selection) */
    setActiveStepIndex: (index: number) => void;
    /** Navigate to next step */
    onNext: () => void;
    /** Navigate to previous step */
    onPrev: () => void;
    /** Jump to a specific index */
    onJumpToIndex: (index: number) => void;
    /** Find and navigate to first incomplete step */
    navigateToFirstIncomplete: (startTime?: Date) => void;
}

/**
 * Manages step navigation state and actions for protocol recording.
 */
export const useStepNavigation = (
    options: UseStepNavigationOptions
): UseStepNavigationResult => {
    const {orderedSteps, onExitFocusMode} = options;
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    const onNext = useMemo(() => () => {
        const newIndex = activeStepIndex + 1;
        if (newIndex < orderedSteps.length) {
            setActiveStepIndex(newIndex);
        } else {
            onExitFocusMode?.();
        }
    }, [activeStepIndex, orderedSteps.length, onExitFocusMode]);

    const onPrev = useMemo(() => () => {
        const newIndex = activeStepIndex - 1;
        if (newIndex >= 0) {
            setActiveStepIndex(newIndex);
        }
    }, [activeStepIndex]);

    const onJumpToIndex = useCallback((index: number) => {
        if (index >= 0 && index < orderedSteps.length) {
            setActiveStepIndex(index);
        } else {
            onExitFocusMode?.();
        }
    }, [orderedSteps.length, onExitFocusMode]);

    const navigateToFirstIncomplete = useCallback(() => {
        const index = orderedSteps.findIndex((step, i) => {
            return step.status === IProtocolStepStatus.INCOMPLETE &&
                (step.kind !== IProtocolStepKind.GROUP ||
                    (i < orderedSteps.length - 1 && orderedSteps[i + 1].status === IProtocolStepStatus.INCOMPLETE)
                );
        });
        if (index >= 0 && index < orderedSteps.length) {
            setActiveStepIndex(index);
        } else {
            setActiveStepIndex(0);
        }
    }, [orderedSteps]);

    return {
        activeStepIndex,
        setActiveStepIndex,
        onNext,
        onPrev,
        onJumpToIndex,
        navigateToFirstIncomplete,
    };
};
